/**
 * SharedAnalyticsView - Read-only analytics snapshot for shared links.
 * Shows metric cards, area chart, channel donut, and top pages.
 */

import { Users, Eye, Clock, TrendingUp, Link2, ExternalLink } from 'lucide-react'
import {
  useSiteAnalytics,
  formatDuration,
  formatNumber
} from '@/hooks/useSiteAnalytics'
import { MetricCard } from './MetricCard'
import { AnalyticsAreaChart } from './AnalyticsAreaChart'
import { AnalyticsDonutChart, DonutLegend } from './AnalyticsDonutChart'
import {
  getPageDisplayName,
  getChannelLabel,
  getChannelColor
} from '@/utils/analyticsFormatters'

interface SharedAnalyticsViewProps {
  projectId: string
  projectName?: string
}

export function SharedAnalyticsView({ projectId, projectName }: SharedAnalyticsViewProps) {
  const { data, loading, error } = useSiteAnalytics({ projectId, dateRange: '30d' })

  if (loading) {
    return (
      <div className="min-h-screen bg-framed-neutral-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-framed-neutral-400 border-t-framed-yellow rounded-full" />
      </div>
    )
  }

  if (error || !data || data.overview.pageviews === 0) {
    return (
      <div className="min-h-screen bg-framed-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 text-framed-neutral-400 mx-auto mb-3" />
          <p className="text-framed-neutral-500">No analytics data available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-framed-neutral-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-framed-neutral-800">
            Analytics Report
          </h1>
          {projectName && (
            <p className="text-framed-neutral-500 mt-1">{projectName}</p>
          )}
          <p className="text-xs text-framed-neutral-500 mt-2">Last 30 days</p>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard
            icon={Users}
            label="Visitors"
            value={formatNumber(data.overview.visitors)}
            subValue={`${data.overview.newVisitorRate}% new`}
            trend={data.trends?.visitors ? {
              direction: data.trends.visitors.direction,
              delta: data.trends.visitors.delta
            } : undefined}
          />
          <MetricCard
            icon={Eye}
            label="Pageviews"
            value={formatNumber(data.overview.pageviews)}
            subValue={`${data.overview.sessions} sessions`}
            trend={data.trends?.pageviews ? {
              direction: data.trends.pageviews.direction,
              delta: data.trends.pageviews.delta
            } : undefined}
          />
          <MetricCard
            icon={TrendingUp}
            label="Bounce Rate"
            value={`${data.overview.bounceRate}%`}
            trend={data.trends?.bounceRate ? {
              direction: data.trends.bounceRate.direction,
              delta: data.trends.bounceRate.delta
            } : undefined}
          />
          <MetricCard
            icon={Clock}
            label="Avg. Duration"
            value={formatDuration(data.overview.avgSessionDuration)}
            trend={data.trends?.avgSessionDuration ? {
              direction: data.trends.avgSessionDuration.direction,
              delta: data.trends.avgSessionDuration.delta
            } : undefined}
          />
        </div>

        {/* Area chart */}
        {data.timeSeries.length > 0 && (
          <div className="bg-framed-neutral-100 rounded-2xl p-5 mb-8">
            <h3 className="text-sm font-medium text-framed-neutral-800 mb-4">
              Visitors Over Time
            </h3>
            <AnalyticsAreaChart
              data={data.timeSeries}
              height={220}
            />
          </div>
        )}

        {/* Channel donut + Top pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {data.channels.length > 0 && (
            <div className="bg-framed-neutral-100 rounded-2xl p-5">
              <h3 className="text-sm font-medium text-framed-neutral-800 mb-4">
                Traffic Channels
              </h3>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-full sm:w-1/2">
                  <AnalyticsDonutChart
                    data={data.channels.slice(0, 8).map(ch => ({
                      name: getChannelLabel(ch.channel),
                      value: ch.count,
                      percentage: ch.percentage,
                      color: getChannelColor(ch.channel)
                    }))}
                    height={160}
                    centerLabel="Visitors"
                    centerValue={formatNumber(data.overview.visitors)}
                  />
                </div>
                <div className="w-full sm:w-1/2">
                  <DonutLegend
                    data={data.channels.slice(0, 8).map(ch => ({
                      name: getChannelLabel(ch.channel),
                      value: ch.count,
                      percentage: ch.percentage,
                      color: getChannelColor(ch.channel)
                    }))}
                  />
                </div>
              </div>
            </div>
          )}

          {data.topPages.length > 0 && (
            <div className="bg-framed-neutral-100 rounded-2xl p-5">
              <h3 className="text-sm font-medium text-framed-neutral-800 mb-4">
                Top Pages
              </h3>
              <div className="space-y-3">
                {data.topPages.slice(0, 5).map(page => {
                  const displayName = getPageDisplayName(page.path, page.title)
                  return (
                    <div key={page.path} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Link2 className="w-3.5 h-3.5 text-framed-neutral-500 flex-shrink-0" />
                        <span className="text-sm text-framed-neutral-700 truncate">{displayName}</span>
                      </div>
                      <div className="text-sm text-right flex-shrink-0 ml-3">
                        <span className="font-medium text-framed-neutral-800">{formatNumber(page.views)}</span>
                        <span className="text-framed-neutral-500 ml-1.5">{Math.round(page.percentage)}%</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center pt-6 border-t border-framed-neutral-300">
          <a
            href="https://www.framed.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-framed-neutral-500 hover:text-framed-neutral-600 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Powered by Framed
          </a>
        </div>
      </div>
    </div>
  )
}
